/**
 * Simple feature flag system using localStorage.
 * Users can disable features via DevTools console:
 *   localStorage.setItem('ev_feature_chunks', 'false')
 *
 * No deployment needed to toggle features.
 */

import { isAndroid } from './platform';

const FLAGS = {
  chunks_enabled: {
    key: 'ev_feature_chunks',
    default: true,
  },
  builtin_lens_enabled: {
    key: 'ev_feature_builtin_lens',
    default: true,
  },
  adaptive_display_enabled: {
    key: 'ev_feature_adaptive',
    default: false,
  },
  ipad_reader_beta: {
    key: 'ev_feature_ipad_reader_beta',
    default: false,
  },
  // F35: Android-only — disable native selection so Chrome's system selection
  // toolbar and Google Contextual Search bar never trigger. Pointer events
  // re-render the selection via .ev-temp-selection overlay and hand off to
  // LensBottomSheet. iOS (where -webkit-touch-callout: none works) and
  // desktop (no problem) keep native selection.
  enhanced_selection: {
    key: 'ev_feature_enhanced_selection',
    default: () => isAndroid(),
  },
  // Deep Reading sidebar (weighted lookup bubbles + notes/highlights waterfall
  // + persistent chat). Off by default — gated rollout while we measure whether
  // users actually engage the right panel. Kill-switch via DevTools:
  //   localStorage.setItem('ev_feature_deep_reading', 'false')
  deep_reading_enabled: {
    key: 'ev_feature_deep_reading',
    default: false,
  },
};

/**
 * Check if a feature flag is enabled.
 * @param {'chunks_enabled' | 'builtin_lens_enabled' | 'adaptive_display_enabled' | 'ipad_reader_beta' | 'enhanced_selection' | 'deep_reading_enabled'} flagName
 * @returns {boolean}
 */
export function isFeatureEnabled(flagName) {
  const flag = FLAGS[flagName];
  if (!flag) return false;
  try {
    const stored = localStorage.getItem(flag.key);
    if (stored !== null) return stored === 'true';
  } catch { /* localStorage unavailable */ }
  return typeof flag.default === 'function' ? !!flag.default() : !!flag.default;
}
